/* eslint-disable no-debugger */
/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-no-constructed-context-values */
// (c) Copyright 2022 HP Development Company, L.P.

import React, { createContext, useContext, useEffect } from 'react'
import { setIceServers, useHyperspace } from '@acs-manageability/webrtc-react'

import { useKvmStatusStore } from '../hooks/KvmStatus'
import {
  connectionClient,
  signalClient,
  resourceAllocationClient,
  turnAuthenticationClient,
} from '../utils'
import { signalServerUrl, allocateDeviceTimeout } from '../constants/env'
import { addListener, removeListener, emit } from '../utils/EventUtils'

export const ConnectionContext = createContext({})

const version = 1
const source = 'client'
const code = 'icicle-client'
const from =
  Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)

let timeoutCheckTask = null

export function ConnectionProvider({ children }) {
  const { isMuted, setIsMuted, isAudioDisabled, toggleMuteAudio } = useHyperspace()

  const [setConnectionStatus, setInited] = useKvmStatusStore((set) => [
    set.setConnectionStatus,
    set.setInited,
  ])
  const [connectStep, errorType, connectedTimeout] = useKvmStatusStore((set) => [
    set.connectStep,
    set.errorType,
    set.connectedTimeout,
  ])

  // const urlParams = new URLSearchParams(window.location.search);
  // const deviceId = urlParams.get("device_id");
  // const sessionToken = urlParams.get("session_token");

  useEffect(() => {
    const onKvmStatusChanged = (data) => {
      const { step, error } = data
      setConnectionStatus({ step, error })
    }
    addListener('kvm_status_changed', onKvmStatusChanged)
    return () => {
      removeListener('kvm_status_changed', onKvmStatusChanged)
    }
  }, [setConnectionStatus])

  useEffect(() => {
    const onClearTimeoutCheckTask = () => {
      console.log('Clear timeout check task.')
      clearTimeout(timeoutCheckTask)
    }
    const onResetTimeoutCheckTask = () => {
      onClearTimeoutCheckTask()
      if (connectStep !== 'establish_stream') {
        console.log('Reset timeout check task.')
        timeoutCheckTask = setTimeout(() => {
          connectedTimeout()
        }, allocateDeviceTimeout || 30000)
      }
    }
    addListener('clear_timeout_check_task', onClearTimeoutCheckTask)
    addListener('reset_timeout_check_task', onResetTimeoutCheckTask)

    return () => {
      removeListener('clear_timeout_check_task', onClearTimeoutCheckTask)
      removeListener('reset_timeout_check_task', onResetTimeoutCheckTask)
    }
  }, [connectStep, connectedTimeout])

  useEffect(() => {
    if (connectStep === 'establish_stream') {
      emit('clear_timeout_check_task', {})
    }
  }, [connectStep])

  useEffect(() => {
    const reSendAllocateMessage = (resource) => {
      if (!errorType) {
        console.log('Resend allocate message.')
        setTimeout(() => {
          resourceAllocationClient.allocateResource(resource, false)
        }, 500)
      } else {
        console.log('Can not resend allocate message, error type:', errorType)
      }
    }
    addListener('resend_allocate_message', reSendAllocateMessage)
    return () => {
      removeListener('resend_allocate_message', reSendAllocateMessage)
    }
  }, [errorType])

  const connect = async (sessionToken, deviceId) => {
    signalClient.setGlobalMessages({
      version: version,
      source: source,
      from: from,
      code: code,
      token: sessionToken,
    })

    setConnectionStatus({ step: 'connection_to_signal' })

    const resource = {
      id: deviceId,
      name: 'My Desktop Web Client',
      selected: true,
    }

    try {
      console.log('Websocket connection init.')
      await connectionClient.connect(signalServerUrl, deviceId, sessionToken, () => {
        emit('resend_allocate_message', resource)
      })
      const iceServers = await turnAuthenticationClient.getTurnCredentials(deviceId, '')
      // console.log("Got IceServers:", JSON.stringify(iceServers));
      setIceServers(iceServers)
      // forceIceTransportRelay();
      setInited(true)

      await resourceAllocationClient.allocateResource(resource, true)
    } catch (error) {
      throw new Error(error)
    }
  }

  // const disconnect = () => {
  // 	connectionClient.disconnectFromServer();
  // };

  // function sendMessage(message) {
  // 	return signalClient.sendMessage(message);
  // }

  return (
    <ConnectionContext.Provider
      value={{
        // isInited,
        connect,
        // disconnect,
        // sendMessage,
        toggleMuteAudio,
        isMuted,
        setIsMuted,
        isAudioDisabled,
      }}
    >
      {children}
    </ConnectionContext.Provider>
  )
}

export const useConnection = () => {
  const context = useContext(ConnectionContext)
  return context
}
